// @ts-check
/* ═══════════════════════ VOICE — Web Speech + MediaRecorder ═══════════════════════
   - Live transkribering via SpeechRecognition (norsk + engelsk).
   - Lagrer rå lyd-blob via MediaRecorder for backup (kan lastes ned senere).
   - Robust: faller tilbake hvis API mangler, gir tydelig feedback.
   ═══════════════════════════════════════════════════════════════════════════════ */

const SR = window.SpeechRecognition || window.webkitSpeechRecognition;

export function isVoiceSupported(){
  return !!SR && !!navigator.mediaDevices?.getUserMedia;
}

/**
 * Starter en opptak/transkriberingssesjon.
 * @param {object} opts
 * @param {string} [opts.lang='nb-NO']  - sprak for transkribering
 * @param {(text:string,isFinal:boolean)=>void} opts.onTranscript - kalt lopende
 * @param {(blob:Blob)=>void} [opts.onAudioReady] - kalt nar lyd-blob er klar
 * @param {(err:string)=>void} [opts.onError]
 * @returns {{stop:()=>Promise<{text:string,blob:Blob|null}>}|null}
 */
export function startDictation({ lang='nb-NO', onTranscript, onAudioReady, onError }){
  if (!isVoiceSupported()) {
    onError?.('Diktering støttes ikke i denne nettleseren. Prøv Safari eller Chrome.');
    return null;
  }

  let finalText = '';
  let interimText = '';
  let stream = null;
  let recorder = null;
  let chunks = [];
  let recognition = null;
  let stopped = false;

  // 1. Speech recognition (live tekst)
  recognition = new SR();
  recognition.lang = lang;
  recognition.interimResults = true;
  recognition.continuous = true;

  recognition.onresult = (e) => {
    interimText = '';
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const r = e.results[i];
      if (r.isFinal) finalText += r[0].transcript + ' ';
      else interimText += r[0].transcript;
    }
    onTranscript?.(finalText + interimText, /*isFinal*/ false);
  };
  recognition.onerror = (e) => {
    if (e.error === 'no-speech' || e.error === 'aborted') return; // ignorer
    onError?.('Diktering feilet: ' + e.error);
  };
  recognition.onend = () => {
    if (!stopped) {
      // auto-restart hvis vi ikke er stoppet (Safari kutter etter ~60s)
      try { recognition.start(); } catch {}
    }
  };

  // 2. MediaRecorder (lyd-backup)
  navigator.mediaDevices.getUserMedia({ audio: true })
    .then((s) => {
      stream = s;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '');
      try {
        recorder = mime ? new MediaRecorder(s, { mimeType: mime }) : new MediaRecorder(s);
      } catch {
        recorder = new MediaRecorder(s);
      }
      recorder.ondataavailable = (e) => { if (e.data?.size > 0) chunks.push(e.data); };
      recorder.start(1000);
      try { recognition.start(); } catch (e) { onError?.('Kunne ikke starte diktering: ' + e.message); }
    })
    .catch((err) => {
      onError?.('Mikrofontilgang nektet: ' + err.message);
    });

  return {
    async stop(){
      stopped = true;
      try { recognition?.stop(); } catch {}
      let blob = null;
      if (recorder && recorder.state !== 'inactive') {
        blob = await new Promise((resolve) => {
          recorder.onstop = () => {
            try {
              const b = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
              resolve(b);
            } catch { resolve(null); }
          };
          recorder.stop();
        });
      }
      try { stream?.getTracks().forEach(t => t.stop()); } catch {}
      onTranscript?.(finalText.trim(), /*isFinal*/ true);
      onAudioReady?.(blob);
      return { text: finalText.trim(), blob };
    }
  };
}
